import React, { useState, useEffect } from 'react';
import { styled } from 'styletron-react';
import { Button } from 'baseui/button';
import { HeadingMedium, HeadingSmall, LabelMedium } from 'baseui/typography';
import { Spinner } from 'baseui/spinner';
import { toaster } from 'baseui/toast';
import { StringColumn, CustomColumn } from 'baseui/data-table';
import { Drawer, SIZE as DRAWER_SIZE } from 'baseui/drawer';
import { FormControl } from 'baseui/form-control';
import { Select } from 'baseui/select';
import { Input } from 'baseui/input';
import { Tag } from 'baseui/tag';

// Type assertions to handle BaseUI compatibility issues
const DrawerComponent = Drawer as any;
const FormControlComponent = FormControl as any;
const SelectComponent = Select as any;
const InputComponent = Input as any;


import { Flex } from '../components/Flex';
import { BaseGrid } from '../components/BaseGrid';
import { isUserAdmin } from '../utils/auth';
import { usePropertyAdmin, useDurationOptions } from '../hooks/usePropertyAdmin';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { EstateGroupDetailsModal } from '../components/PropertyAdmin/EstateGroupDetailsModal';

const Container = styled('div', {
  padding: '24px',
  backgroundColor: '#fff',
  minHeight: 'calc(100vh - 70px)',
});

const Header = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
});

const RequestButton = styled(Button, {
  marginLeft: 'auto',
});

const DrawerContent = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: '24px 24px 60px 24px', // Extra bottom padding for buttons
  height: '100%',
  overflow: 'auto',
  boxSizing: 'border-box',
});

const DrawerHeader = styled('div', {
  marginBottom: '24px',
  borderBottom: '1px solid #E2E8F0',
  paddingBottom: '16px',
});

const FormSection = styled('div', {
  marginBottom: '24px',
});

// Import types from the hook
import type { PropertyAdminEstate, EstateGroupAccess, EstateRequest, NewAccessRequest } from '../hooks/usePropertyAdmin';

export const PropertyAdmin = () => {
  const { cognitoId } = useUserPermissions();
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [showRequestDrawer, setShowRequestDrawer] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEstate, setSelectedEstate] = useState<PropertyAdminEstate | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Use the property admin hook — scoped to the current logged-in user
  const {
    authorizedEstates,
    pendingRequests,
    availableEstates,
    estateGroups,
    isLoadingAuthorizedEstates,
    isLoadingPendingRequests,
    isLoadingAvailableEstates,
    isLoadingEstateGroups,
    isSubmittingRequest,
    isUpdatingEstate,
    isRemovingEstate,
    errorAuthorizedEstates,
    errorPendingRequests,
    errorAvailableEstates,
    errorEstateGroups,
    submitAccessRequest,
    updateAuthorizedEstate,
    removeAuthorizedEstate,
    stats,
  } = usePropertyAdmin(currentUserId);

  const durationOptions = useDurationOptions();

  const [newRequest, setNewRequest] = useState<NewAccessRequest>({
    estateId: 0,
    duration: '',
    reason: '',
  });

  // Additional state for drawer form
  const [selectedEstateGroup, setSelectedEstateGroup] = useState<number>(0);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  // Check admin status
  useEffect(() => {
    const init = async () => {
      try {
        const adminStatus = await isUserAdmin();
        setIsAdmin(adminStatus);
        // Look up user ID from DB using cognitoId
        if (cognitoId) {
          const { defaultInstance } = await import('../api/httpClient');
          const resp = await defaultInstance({ method: 'GET', url: `/user?cognitoid=${cognitoId}&limit=1` });
          const users = (resp as any)?.data?.data ?? (resp as any)?.data ?? [];
          if (users.length > 0) setCurrentUserId(users[0].id);
        }
      } catch (error) {
        console.error('Error initializing:', error);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [cognitoId]);

  const handleRequestAccess = () => {
    if (!selectedEstateGroup || !newRequest.estateId) {
      toaster.negative('Please select an estate group and estate', {
        autoHideDuration: 3000,
      });
      return;
    }

    if (!currentUserId) {
      toaster.negative('User ID not found. Please try logging in again.', {
        autoHideDuration: 3000,
      });
      return;
    }

    submitAccessRequest({
      estate_id: newRequest.estateId,
      estate_group_id: selectedEstateGroup,
      property_admin_id: currentUserId,
      requested_duration: '',
      documents: [],
    });
    setNewRequest({ estateId: 0, duration: '', reason: '' });
    setSelectedEstateGroup(0);
    setShowRequestDrawer(false);
  };

  const resolveRawEstateId = (estate: any): number =>
    typeof estate.estate_id === 'object' ? estate.estate_id?.id : estate.estate_id;

  const handleRemoveAccess = (estateId: number) => {
    if (window.confirm('Are you sure you want to remove access to this estate?')) {
      // Find the authorized estate record
      const authorizedEstate = authorizedEstates.find(estate => resolveRawEstateId(estate) === estateId);
      if (authorizedEstate) {
        removeAuthorizedEstate(authorizedEstate.id.toString());
      } else {
        toaster.negative('Estate not found', {
          autoHideDuration: 3000,
        });
      }
    }
  };

  const handleRequestExtension = (estateId: number) => {
    // Find the authorized estate record
    const authorizedEstate = authorizedEstates.find(estate => resolveRawEstateId(estate) === estateId);
    if (authorizedEstate) {
      // Calculate new expiration date (extend by 1 year)
      const currentExpiration = new Date(authorizedEstate.expiration_date);
      const newExpiration = new Date(currentExpiration.getTime() + 365 * 24 * 60 * 60 * 1000);
      
      const updateData = {
        authorized_until: newExpiration.toISOString().split('T')[0],
      };
      
      updateAuthorizedEstate(authorizedEstate.id.toString(), updateData);
    } else {
      toaster.negative('Estate not found', {
        autoHideDuration: 3000,
      });
    }
  };

  const handleEstateClick = (estate: PropertyAdminEstate) => {
    setSelectedEstate(estate);
    setShowDetailsModal(true);
  };

  const getStatusColor = (status: string) => {
    if (!status) return 'neutral';
    switch (status.toLowerCase()) {
      case 'approved': return 'positive';
      case 'rejected': return 'negative';
      case 'pending': return 'warning';
      default: return 'neutral';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getDaysRemaining = (dateString: string) => {
    const today = new Date();
    const endDate = new Date(dateString);
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Combined columns for estates and requests
  const combinedColumns = [
    StringColumn({
      title: 'Estate Group',
      maxWidth: 200,
      mapDataToValue: (row: any) => row?.estateGroupName || 'N/A',
    }),
    StringColumn({
      title: 'Estate',
      maxWidth: 200,
      mapDataToValue: (row: any) => row?.estateName || 'N/A',
    }),
    CustomColumn({
      title: 'Status',
      maxWidth: 120,
      mapDataToValue: (row: any) => row,
      renderCell: ({ value }: { value: any }) => {
        if (!value) return <Tag color="neutral">Unknown</Tag>;
        
        if (value.type === 'estate') {
          return (
            <Tag color={value.canCreatePasses ? 'positive' : 'negative'}>
              {value.canCreatePasses ? 'Active' : 'Inactive'}
            </Tag>
          );
        } else if (value.type === 'request') {
          return (
            <Tag color={getStatusColor(value.status) as any}>
              {value.status?.charAt(0).toUpperCase() + value.status?.slice(1) || 'Unknown'}
            </Tag>
          );
        } else {
          return <Tag color="neutral">Unknown</Tag>;
        }
      },
    }),
    StringColumn({
      title: 'Expiration Date',
      maxWidth: 150,
      mapDataToValue: (row: any) => row?.expirationDate ? formatDate(row.expirationDate) : 'N/A',
    }),
    CustomColumn({
      title: 'Actions',
      maxWidth: 200,
      mapDataToValue: (row: any) => row,
      renderCell: ({ value }: { value: any }) => {
        if (!value) return <div>No actions</div>;
        
        if (value.type === 'estate') {
          return (
            <Flex gap="8px">
              <Button
                kind="secondary"
                size="compact"
                onClick={() => handleRemoveAccess(value.estateId)}
              >
                Remove
              </Button>
              <Button
                kind="secondary"
                size="compact"
                onClick={() => handleRequestExtension(value.estateId)}
              >
                Request Extension
              </Button>
            </Flex>
          );
        } else if (value.type === 'request') {
          return (
            <Tag color="warning">
              Pending Approval
            </Tag>
          );
        } else {
          return <div>No actions</div>;
        }
      },
    }),
  ];

  if (isLoading) {
    return (
      <Container>
        <Flex justifyContent="center" alignItems="center" height="400px">
          <Spinner size={48} />
        </Flex>
      </Container>
    );
  }

  // Helper to extract name from a field that may be a number or an expanded object
  const expandedName = (field: any): string | undefined =>
    typeof field === 'object' && field !== null ? field.name : undefined;

  // Transform real API data for table display (using user_estate structure)
  // The API may return estate_group as a top-level field OR expand estate_id / estate_group_id into objects
  const resolveEstateName = (record: any) =>
    record.estate?.name
    || expandedName(record.estate_id)
    || 'N/A';

  const resolveGroupName = (record: any) =>
    record.estate_group?.name
    || record.estate?.estate_group?.name
    || expandedName(record.estate_group_id)
    || 'N/A';

  const resolveGroupId = (record: any) =>
    record.estate_group?.id
    || (typeof record.estate_group_id === 'object' ? record.estate_group_id?.id : record.estate_group_id)
    || record.estate?.estate_group_id;

  // StatefulDataTable passes row.data to mapDataToValue, so display fields must live in data
  const combinedData = [
    ...authorizedEstates.map(estate => ({
      id: `estate-${resolveRawEstateId(estate)}-${estate.user_id}`,
      data: {
        estateGroupName: resolveGroupName(estate),
        estateName: resolveEstateName(estate),
        expirationDate: estate.expiration_date,
        type: 'estate',
        canCreatePasses: estate.status_id === 2,
        estateGroupId: resolveGroupId(estate),
        estateId: resolveRawEstateId(estate),
      },
    })),
    ...pendingRequests.map(request => ({
      id: `request-${resolveRawEstateId(request)}-${request.user_id}`,
      data: {
        estateGroupName: resolveGroupName(request),
        estateName: resolveEstateName(request),
        expirationDate: request.created_at,
        type: 'request',
        status: 'Pending',
        estateGroupId: resolveGroupId(request),
        estateId: resolveRawEstateId(request),
      },
    }))
  ];

  return (
    <Container>
      <Header>
        <HeadingMedium>My Estates</HeadingMedium>
        <RequestButton
          onClick={() => setShowRequestDrawer(true)}
          overrides={{
            BaseButton: {
              style: {
                backgroundColor: 'var(--tenant-primary, #111827)',
                ':hover': { backgroundColor: 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)' },
              },
            },
          }}
        >
          Request Estate
        </RequestButton>
      </Header>

      <BaseGrid
        rows={combinedData}
        columns={combinedColumns}
        loading={isLoadingAuthorizedEstates || isLoadingPendingRequests}
        emptyMessage="No estates or requests available"
        searchable
        filterable={false}
        loadingMessage="Loading estates and requests..."
      />

      {/* Request New Access Drawer */}
      <DrawerComponent
        isOpen={showRequestDrawer}
        onClose={() => setShowRequestDrawer(false)}
        anchor="right"
        overrides={{
          Root: {
            style: { zIndex: 10000 },
          },
          Backdrop: {
            style: { zIndex: 10000 },
          },
          DrawerContainer: {
            style: {
              width: "45vw",
              maxWidth: "650px",
              minWidth: "400px",
              zIndex: 10001,
            },
          },
          DrawerBody: {
            style: {
              padding: "0",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            },
          },
        }}
      >
        <DrawerContent>
          <DrawerHeader>
            <HeadingMedium>Request New Estate Access</HeadingMedium>
          </DrawerHeader>

          <FormSection>
            <FormControlComponent label="Select Estate Group">
              <SelectComponent
                options={estateGroups.map(group => ({
                  label: group.name,
                  id: group.id,
                }))}
                value={selectedEstateGroup ? [{
                  label: estateGroups.find(g => g.id === selectedEstateGroup)?.name || '',
                  id: selectedEstateGroup,
                }] : []}
                onChange={({ value }: any) => setSelectedEstateGroup(value[0]?.id || 0)}
                placeholder="Choose an estate group"
                isLoading={isLoadingEstateGroups}
                overrides={{ Popover: { props: { overrides: { Body: { style: { zIndex: 10002 } } } } } }}
              />
            </FormControlComponent>
          </FormSection>

          <FormSection>
            <FormControlComponent label="Select Estate">
              <SelectComponent
                options={availableEstates
                  .filter(estate => !selectedEstateGroup || estate.estate_group_id === selectedEstateGroup)
                  .map(estate => ({
                    label: estate.name,
                    id: estate.id,
                  }))}
                value={newRequest.estateId ? [{
                  label: availableEstates.find(e => e.id === newRequest.estateId)?.name || '',
                  id: newRequest.estateId,
                }] : []}
                onChange={({ value }: any) => setNewRequest(prev => ({ ...prev, estateId: value[0]?.id || 0 }))}
                placeholder={selectedEstateGroup ? "Choose an estate" : "First select an estate group"}
                isLoading={isLoadingAvailableEstates}
                disabled={!selectedEstateGroup}
                overrides={{ Popover: { props: { overrides: { Body: { style: { zIndex: 10002 } } } } } }}
              />
            </FormControlComponent>
          </FormSection>

          <Flex justifyContent="flex-end" gap="12px" marginTop="24px">
            <Button
              kind="secondary"
              onClick={() => setShowRequestDrawer(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRequestAccess}
              disabled={!selectedEstateGroup || !newRequest.estateId || isSubmittingRequest}
              isLoading={isSubmittingRequest}
              overrides={{
                BaseButton: {
                  style: {
                    backgroundColor: 'var(--tenant-primary, #111827)',
                    ':hover': { backgroundColor: 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)' },
                  },
                },
              }}
            >
              Submit Request
            </Button>
          </Flex>
        </DrawerContent>
      </DrawerComponent>

      {/* Estate Group Details Modal */}
      <EstateGroupDetailsModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedEstate(null);
        }}
        estate={selectedEstate}
      />
    </Container>
  );
}; 